import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PromotionCard } from "@/components/PromotionCard";
import { CountUp } from "@/components/CountUp";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trophy, TrendingUp, Wallet, Trash2, History, Activity } from "lucide-react";
import { eur, formatDate } from "@/lib/format";
import { buildTimeline } from "@/lib/timeline";
import { SalesTimelineChart } from "@/components/SalesTimelineChart";
import { PrizeUnlockCelebration } from "@/components/PrizeUnlockCelebration";

export function AgentView({ title = "I miei progressi", subtitle }) {
  const [dash, setDash] = useState(null);
  const [sales, setSales] = useState([]);
  const [history, setHistory] = useState([]);
  const [regPromo, setRegPromo] = useState(null);
  const [client, setClient] = useState("");
  const [note, setNote] = useState("");
  const [qty, setQty] = useState(1);
  const [saving, setSaving] = useState(false);
  const [celebration, setCelebration] = useState({ open: false, prizeName: "", amount: 0 });
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(() => {
    api.get("/dashboard").then((r) => setDash(r.data)).catch(() => setDash({ period: null, promotions: [] }));
    api.get("/sales").then((r) => setSales(r.data)).catch(() => setSales([]));
    api.get("/prizes/history").then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitSale = async () => {
    setSaving(true);
    try {
      const res = await api.post("/sales", { promotion_id: regPromo.id, client_name: client || null, note: note || null, quantity: regPromo.type === "pezzi" ? (parseInt(qty) || 1) : 1 });
      if (res.data.just_unlocked) {
        setCelebration({ open: true, prizeName: regPromo.prize, amount: res.data.count * regPromo.value_per_sale });
      } else {
        toast.success("Vendita registrata");
      }
      setRegPromo(null); setClient(""); setNote(""); setQty(1);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteSale = async (id) => {
    try {
      await api.delete(`/sales/${id}`);
      toast.success("Vendita eliminata");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!dash) {
    return (
      <div className="grid md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-52 bg-[#FFFFFF]" />)}
      </div>
    );
  }

  const promos = dash.promotions || [];
  const totalPrize = promos.reduce((s, p) => s + p.prize_value, 0);
  const unlocked = promos.filter((p) => p.achieved).length;
  const totalSales = promos.reduce((s, p) => s + p.count, 0);

  return (
    <div className="space-y-8" data-testid="agent-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{title}</h1>
          {subtitle && <p className="text-[#5A6B85] mt-1">{subtitle}</p>}
        </div>
        {dash.period && (
          <Badge className="bg-[#0B6BCB]/15 text-[#0B6BCB] border-[#0B6BCB]/30" data-testid="active-period-badge">
            {dash.period.name} · {formatDate(dash.period.start_date)} – {formatDate(dash.period.end_date)}
          </Badge>
        )}
      </div>

      {!dash.period ? (
        <Card className="p-10 bg-[#FFFFFF] border-[#DCE7F5] text-center text-[#5A6B85]">Nessuna campagna attiva al momento.</Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Stat icon={Wallet} label="Premio totale maturato" testid="agent-reward-total">
              <CountUp value={totalPrize} prefix="€ " decimals={2} className="text-[#0B6BCB]" />
            </Stat>
            <Stat icon={Trophy} label="Promozioni sbloccate" testid="agent-unlocked-count">
              <CountUp value={unlocked} className="text-[#0F1E33]" /> <span className="text-[#94A3B8] text-2xl">/ {promos.length}</span>
            </Stat>
            <Stat icon={TrendingUp} label="Vendite totali" testid="agent-total-sales">
              <CountUp value={totalSales} className="text-[#0F1E33]" />
            </Stat>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {promos.map((p, i) => <PromotionCard key={p.id} promo={p} index={i} onRegister={setRegPromo} />)}
          </div>

          <Card className="bg-[#FFFFFF] border-[#DCE7F5] overflow-hidden">
            <div className="p-5 border-b border-[#DCE7F5] font-display font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} /> Andamento vendite e premi
            </div>
            <div className="p-4">
              <SalesTimelineChart data={buildTimeline(sales, promos)} />
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[#FFFFFF] border-[#DCE7F5] overflow-hidden">
              <div className="p-5 border-b border-[#DCE7F5] font-display font-semibold flex items-center gap-2">
                <History className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} /> Le mie vendite
              </div>
              <div className="max-h-80 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                      <TableHead className="text-[#5A6B85]">Promozione</TableHead>
                      <TableHead className="text-[#5A6B85]">Cliente</TableHead>
                      <TableHead className="text-[#5A6B85]">Data</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sales.map((s) => (
                      <TableRow key={s.id} className="border-[#DCE7F5]" data-testid={`sale-row-${s.id}`}>
                        <TableCell className="font-medium">
                          {s.promotion_name}
                          {s.quantity > 1 && <span className="ml-1.5 text-xs text-[#0B6BCB] font-semibold">{s.quantity} pz</span>}
                        </TableCell>
                        <TableCell className="text-[#5A6B85]">{s.client_name || "-"}</TableCell>
                        <TableCell className="text-[#5A6B85] text-sm">{formatDate(s.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[#5A6B85] hover:text-red-600" onClick={() => setConfirmDel(s)} data-testid={`delete-sale-${s.id}`}>
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {sales.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-[#5A6B85] py-8">Nessuna vendita registrata</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </Card>

            <Card className="bg-[#FFFFFF] border-[#DCE7F5] overflow-hidden">
              <div className="p-5 border-b border-[#DCE7F5] font-display font-semibold flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} /> Storico premi
              </div>
              <div className="max-h-80 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                      <TableHead className="text-[#5A6B85]">Campagna</TableHead>
                      <TableHead className="text-[#5A6B85]">Promo</TableHead>
                      <TableHead className="text-[#5A6B85] text-right">Premio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.promotion_id} className="border-[#DCE7F5]" data-testid={`history-row-${h.promotion_id}`}>
                        <TableCell className="text-[#5A6B85] text-sm">{h.period_name}</TableCell>
                        <TableCell className="font-medium">{h.promotion_name} <span className="text-[#94A3B8] text-xs">({h.count}/{h.target})</span></TableCell>
                        <TableCell className="text-right font-semibold" >
                          <span className={h.value > 0 ? "text-[#0B6BCB]" : "text-[#94A3B8]"}>{eur(h.value)}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                    {history.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-[#5A6B85] py-8">Nessun premio ancora</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        </>
      )}

      <Dialog open={!!regPromo} onOpenChange={(o) => !o && setRegPromo(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]" data-testid="register-sale-dialog">
          <DialogHeader><DialogTitle className="font-display">Registra vendita · {regPromo?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {regPromo?.type === "pezzi" && (
              <div className="space-y-1.5">
                <Label className="text-[#33435E]">Pezzi venduti</Label>
                <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className="bg-[#F5F9FF] border-[#DCE7F5]" data-testid="sale-quantity-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-[#33435E]">Cliente (opzionale)</Label>
              <Input value={client} onChange={(e) => setClient(e.target.value)} placeholder="Nome cliente" className="bg-[#F5F9FF] border-[#DCE7F5]" data-testid="sale-client-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#33435E]">Note (opzionale)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className="bg-[#F5F9FF] border-[#DCE7F5]" data-testid="sale-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitSale} disabled={saving} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="sale-submit-button">
              {saving ? "Salvataggio..." : "Registra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PrizeUnlockCelebration
        open={celebration.open}
        prizeName={celebration.prizeName}
        amount={celebration.amount}
        onClose={() => setCelebration((c) => ({ ...c, open: false }))}
      />

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]" data-testid="confirm-delete-sale-dialog">
          <DialogHeader><DialogTitle className="font-display">Eliminare la vendita?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5A6B85]">
            Stai per eliminare la vendita registrata su <strong className="text-[#0F1E33]">{confirmDel?.promotion_name}</strong>
            {confirmDel?.quantity > 1 && <> ({confirmDel.quantity} pezzi)</>}
            {confirmDel?.client_name ? <> per il cliente <strong className="text-[#0F1E33]">{confirmDel.client_name}</strong></> : null}.
            L'operazione non può essere annullata e il conteggio del premio verrà aggiornato.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)} className="text-[#5A6B85] hover:bg-[#EEF4FC]" data-testid="cancel-delete-sale-btn">Annulla</Button>
            <Button onClick={() => deleteSale(confirmDel.id)} className="bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="confirm-delete-sale-btn">
              <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={2} /> Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, children, testid }) {
  return (
    <Card className="p-6 bg-[#FFFFFF] border-[#DCE7F5]" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#5A6B85] mb-3">
        <Icon className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-wide">{label}</span>
      </div>
      <div className="font-display font-bold text-3xl tracking-tight">{children}</div>
    </Card>
  );
}
